import { CANVAS_WIDTH } from "../../game/constants";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { RARITY_COLORS } from "../../economy/characters";
import {
  GAME_FONT,
  MENU_TEXT_PRIMARY,
  resolvePhoneMetrics,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface UsernamePickDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  menuPhaseEnterMs: number;
  nowMs: () => number;
  drawCharacterPortrait: (path: string, x: number, y: number, w: number, h: number) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawUsernamePickScreen(deps: UsernamePickDeps, model: RenderModel): UiRects {
  const { ctx, canvasHeight: canvasH, menuPhaseEnterMs, nowMs, drawCharacterPortrait } = deps;
  const uiRects: UiRects = {};
  const px = 2;
  const phoneMetrics = resolvePhoneMetrics();
  const isEditing = model.username != null;

  // ── Phone shell (same as pause menu -- no dock, no tabs) ──
  const phoneW = Math.min(CANVAS_WIDTH - 24, 340);
  const phoneH = canvasH + 40;
  const phoneX = CANVAS_WIDTH / 2 - phoneW / 2;
  const phoneY = 80;
  const cornerR = 12;
  const bezelW = 6;
  const scrX = phoneX + bezelW;
  const scrY = phoneY + bezelW + 20;
  const scrW = phoneW - bezelW * 2;
  const scrH = canvasH - scrY - 10;
  const contentX = scrX + 8;
  const contentW = scrW - 16;

  // Slide-up animation
  const slideMs = nowMs() - menuPhaseEnterMs;
  const slideProg = Math.min(1, slideMs / 300);
  const slideEase = 1 - (1 - slideProg) * (1 - slideProg);
  const slideOffset = (1 - slideEase) * (canvasH - phoneY + 20);
  ctx.save();
  ctx.translate(0, slideOffset);

  // Outer phone body
  ctx.beginPath();
  ctx.moveTo(phoneX + cornerR, phoneY);
  ctx.arcTo(phoneX + phoneW, phoneY, phoneX + phoneW, phoneY + cornerR, cornerR);
  ctx.lineTo(phoneX + phoneW, phoneY + phoneH);
  ctx.lineTo(phoneX, phoneY + phoneH);
  ctx.lineTo(phoneX, phoneY + cornerR);
  ctx.arcTo(phoneX, phoneY, phoneX + cornerR, phoneY, cornerR);
  ctx.closePath();
  ctx.fillStyle = "#1a2030";
  ctx.fill();
  ctx.strokeStyle = "#3a4a6a";
  ctx.lineWidth = px;
  ctx.stroke();

  // Inner bezel
  ctx.beginPath();
  ctx.moveTo(phoneX + px + cornerR - 1, phoneY + px);
  ctx.arcTo(phoneX + phoneW - px, phoneY + px, phoneX + phoneW - px, phoneY + px + cornerR - 1, cornerR - 1);
  ctx.lineTo(phoneX + phoneW - px, phoneY + phoneH - px);
  ctx.lineTo(phoneX + px, phoneY + phoneH - px);
  ctx.lineTo(phoneX + px, phoneY + px + cornerR - 1);
  ctx.arcTo(phoneX + px, phoneY + px, phoneX + px + cornerR - 1, phoneY + px, cornerR - 1);
  ctx.closePath();
  ctx.strokeStyle = "#2a3a55";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Screen area
  ctx.fillStyle = "#0c1624";
  ctx.fillRect(scrX, scrY, scrW, scrH);
  // Scanlines
  ctx.save();
  ctx.globalAlpha = 0.03;
  for (let sy = scrY; sy < scrY + scrH; sy += 3) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(scrX, sy, scrW, 1);
  }
  ctx.restore();

  // Status bar
  const statusY = phoneY + bezelW + 2;
  const statusH = 16;
  const d = new Date();
  const timeStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  ctx.fillStyle = "#5a7a9a";
  ctx.font = `bold 7px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(timeStr, phoneX + bezelW + 6, statusY + statusH / 2);
  const sigBarX = phoneX + phoneW - bezelW - 30;
  const sigBarY = statusY + 4;
  for (let b = 0; b < 4; b++) {
    const bh = 3 + b * 2;
    ctx.fillStyle = "#4dff88";
    ctx.fillRect(sigBarX + b * 5, sigBarY + (10 - bh), 3, bh);
  }
  const batX = phoneX + phoneW - bezelW - 8;
  ctx.fillStyle = "#4dff88";
  ctx.fillRect(batX, sigBarY + 2, 5, 7);
  ctx.fillRect(batX + 1, sigBarY, 3, 2);

  // ── Screen content (clipped) ──
  ctx.save();
  ctx.beginPath();
  ctx.rect(scrX, scrY, scrW, scrH);
  ctx.clip();

  const centerX = scrX + scrW / 2;
  const ctaH = phoneMetrics.ctaH;
  const skin = model.onboardingSkin;
  const gap = 10;
  const titleH = phoneMetrics.titleFontPx;
  const errorH = model.versusError ? phoneMetrics.helperFontPx + gap : 0;
  const showSkin = !isEditing && skin != null;

  // ── Starter skin portrait -- pinned near top of screen, larger ──
  if (showSkin && skin != null) {
    const portraitSize = 80;
    const portraitX = centerX - portraitSize / 2;
    const portraitY = scrY + phoneMetrics.topPad + 4;

    // Tinted background
    roundedRectPath(ctx, portraitX, portraitY, portraitSize, portraitSize, 8);
    ctx.fillStyle = skin.color;
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;

    drawCharacterPortrait(skin.imageSmallPath, portraitX, portraitY, portraitSize, portraitSize);

    // Border
    const rarityColor = (RARITY_COLORS as Record<string, string>)[skin.rarity] ?? "#3a4870";
    ctx.strokeStyle = rarityColor;
    ctx.lineWidth = 2;
    roundedRectPath(ctx, portraitX, portraitY, portraitSize, portraitSize, 8);
    ctx.stroke();
    ctx.lineWidth = 1;
  }

  // ── Title + input + button -- vertically centered in lower portion ──
  const formH = titleH + gap + errorH + ctaH + gap + ctaH;
  const formTopMin = showSkin ? scrY + phoneMetrics.topPad + 80 + 4 + gap : scrY + phoneMetrics.topPad;
  const formAvailH = scrY + scrH - formTopMin;
  let curY = formTopMin + Math.max(0, (formAvailH - formH) / 2);

  // Title
  ctx.fillStyle = isEditing ? "#64d8ff" : MENU_TEXT_PRIMARY;
  ctx.font = `bold ${phoneMetrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(isEditing ? "EDIT USERNAME" : "WHAT'S YOUR USERNAME?", centerX, curY);
  curY += titleH + gap;

  // Error message
  if (model.versusError) {
    ctx.fillStyle = "#ff6b6b";
    ctx.font = `bold ${phoneMetrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(model.versusError, centerX, curY);
    curY += phoneMetrics.helperFontPx + gap;
  }

  // Input field (HTML overlay positioned here) -- same height as button
  const inputRect: Rect = { x: contentX, y: curY, w: contentW, h: ctaH };
  uiRects.usernameInput = inputRect;
  curY += ctaH + gap;

  // CONFIRM / SAVE button
  const claimRect: Rect = { x: contentX, y: curY, w: contentW, h: ctaH };
  uiRects.usernameClaim = claimRect;
  const btnColor = isEditing ? "#64d8ff" : "#4dff88";
  ctx.fillStyle = isEditing ? "#0a1a2a" : "#0a2a10";
  ctx.fillRect(claimRect.x, claimRect.y, claimRect.w, claimRect.h);
  ctx.strokeStyle = btnColor;
  ctx.lineWidth = 1;
  ctx.strokeRect(claimRect.x, claimRect.y, claimRect.w, claimRect.h);
  ctx.fillStyle = btnColor;
  ctx.font = `bold ${phoneMetrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(isEditing ? "SAVE" : "CONFIRM", claimRect.x + claimRect.w / 2, claimRect.y + claimRect.h / 2);

  ctx.restore(); // end screen clip
  ctx.restore(); // end slide translate

  return uiRects;
}
